// src/screens/CompleteSignupScreen.js
// Shown to users who are signed in (e.g. first Google login) but have no
// profile yet. Collects the consents the platform must record (terms,
// 18+) and the optional CEP, then creates the profile on the server.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { createProfile, markCreatingProfile } from '../utils/createProfile';

export default function CompleteSignupScreen() {
    const { user, setModal, signOut } = useAuth();
    const navigate = useNavigate();
    const [cep, setCep] = useState('');
    const [isOver18, setIsOver18] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [saving, setSaving] = useState(false);

    const canSubmit = isOver18 && agreedToTerms && !saving;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit || !user) return;
        setSaving(true);
        markCreatingProfile(true);
        try {
            await createProfile(user, { cep });
            // useAuth sees the new profile and opens the app.
            navigate('/home', { replace: true });
        } catch (error) {
            console.error('[CompleteSignup] profile creation failed', error);
            setModal({ title: 'Não foi possível concluir', message: 'Tente novamente em instantes.' });
        } finally {
            markCreatingProfile(false);
            setSaving(false);
        }
    };

    const inputClass = 'w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500';

    return (
        <div className="flex flex-col justify-center items-center min-h-screen p-6 bg-gray-50">
            <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-xl">
                <h1 className="text-2xl font-bold text-green-700 mb-2 text-center">Falta pouco!</h1>
                <p className="text-gray-600 mb-6 text-center text-sm">
                    Você entrou como <strong className="text-gray-800 break-all">{user?.email || user?.phoneNumber}</strong>.
                    Confirme os dados abaixo para criar sua conta.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <label className="block">
                        <span className="block text-sm font-medium text-gray-700 mb-1">CEP (opcional)</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="postal-code"
                            value={cep}
                            onChange={(e) => setCep(e.target.value)}
                            placeholder="00000-000"
                            maxLength={9}
                            className={inputClass}
                        />
                        <span className="block text-xs text-gray-500 mt-1">Ajuda a mostrar itens perto de você.</span>
                    </label>

                    <div className="space-y-3 text-sm text-gray-700">
                        <label className="flex items-start gap-2">
                            <input type="checkbox" checked={isOver18} onChange={(e) => setIsOver18(e.target.checked)} className="mt-0.5 h-4 w-4 accent-green-600" />
                            <span>Confirmo que tenho 18 anos ou mais.</span>
                        </label>
                        <label className="flex items-start gap-2">
                            <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="mt-0.5 h-4 w-4 accent-green-600" />
                            <span>
                                Li e concordo com os{' '}
                                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Termos de Uso</a>{' '}e a{' '}
                                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Política de Privacidade</a>.
                            </span>
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={!canSubmit}
                        className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? 'Criando sua conta...' : 'Criar minha conta'}
                    </button>
                </form>

                <p className="mt-6 text-center text-sm text-gray-600">
                    Não é você? <button type="button" onClick={signOut} className="text-green-700 font-semibold hover:underline">Sair</button>
                </p>
            </div>
        </div>
    );
}
